import { useState, useEffect, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import {
    Lock, Trash2, Plus, Save, RefreshCw,
    AlertCircle, CheckCircle2, Info,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { FORECAST_CONFIG_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

// ── constants ─────────────────────────────────────────────────────────────────

const STAGE_TYPE_OPTIONS = [
    { value: "open", label: "Open" },
    { value: "won",  label: "Won"  },
    { value: "lost", label: "Lost" },
];

const STAGE_TYPE_STYLE = {
    open: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    won:  "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    lost: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

// ── helpers ───────────────────────────────────────────────────────────────────

const clamp = (v, min, max) => Math.min(max, Math.max(min, Number(v) || 0));

const nextOrder = (stages) =>
    stages.length ? Math.max(...stages.map(s => s.order)) + 1 : 1;

// ── ProbabilityInput ──────────────────────────────────────────────────────────

const ProbabilityInput = ({ value, onChange, locked }) => {
    if (locked) {
        return (
            <div className="flex items-center gap-1.5">
                <Lock className="h-3 w-3 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{value}%</span>
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-0.5 w-24">
            <div className="flex items-center gap-1">
                <input
                    type="number"
                    min={0}
                    max={100}
                    value={value}
                    onChange={e => onChange(clamp(e.target.value, 0, 100))}
                    className="w-16 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1
                               bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200
                               focus:outline-none focus:ring-1 focus:ring-sidebar-primary"
                />
                <span className="text-xs text-gray-500">%</span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1">
                <div
                    className={cn(
                        "h-1 rounded-full transition-all",
                        value >= 70 ? "bg-green-500" : value >= 40 ? "bg-yellow-500" : "bg-red-400"
                    )}
                    style={{ width: `${value}%` }}
                />
            </div>
        </div>
    );
};

// ── ScenarioCard ──────────────────────────────────────────────────────────────

const ScenarioCard = ({ title, description, colorClass, children, example }) => (
    <div className={cn("rounded-lg border p-4 flex flex-col gap-3", colorClass)}>
        <div>
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        </div>
        {children}
        {example && (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">{example}</p>
        )}
    </div>
);

// ── main component ─────────────────────────────────────────────────────────────

const ForecastConfig = () => {
    const { fetch, update } = useApiMethods();

    const [stages,   setStages]   = useState([]);
    const [scenario, setScenario] = useState({ best_case_adjustment: 15, worst_case_adjustment: -20 });
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [errors,   setErrors]   = useState([]);

    const load = useCallback(() => {
        setLoading(true);
        fetch(FORECAST_CONFIG_URL)
            .then(res => {
                if (res?.status === "success") {
                    setStages(res.data.stages.map((s, i) => ({ ...s, _key: Date.now() + i })));
                    setScenario(res.data.scenario);
                } else {
                    ToastMsg("error", res?.message || "Failed to load config");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load config"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    // ── stage mutations ───────────────────────────────────────────────────────

    const updateStage = (key, field, value) =>
        setStages(prev => prev.map(s => s._key === key ? { ...s, [field]: value } : s));

    const deleteStage = (key) =>
        setStages(prev => prev.filter(s => s._key !== key));

    const addStage = () => {
        const locked = stages.filter(s => s.is_locked);
        const open   = stages.filter(s => !s.is_locked);
        const newStage = {
            _key:                Date.now(),
            stage_name:          "",
            probability:         25,
            stage_type:          "open",
            included_in_forecast: true,
            order:               nextOrder(open),
            is_locked:           false,
        };
        // Insert before locked rows
        setStages([...open, newStage, ...locked]);
    };

    // ── validate ─────────────────────────────────────────────────────────────

    const validate = (stageList) => {
        const errs = [];

        const names = stageList.map(s => s.stage_name.trim().toLowerCase()).filter(Boolean);
        if (new Set(names).size !== names.length)
            errs.push("Duplicate stage names are not allowed.");

        const openIncluded = stageList.filter(s => s.stage_type === "open" && s.included_in_forecast);
        if (openIncluded.length === 0)
            errs.push("At least one Open stage must be included in the forecast.");

        for (const s of stageList) {
            if (!s.stage_name.trim())
                errs.push("All stages must have a name.");
            if (!s.is_locked && (s.probability < 0 || s.probability > 100))
                errs.push(`Probability for "${s.stage_name}" must be 0–100.`);
        }

        if (!stageList.find(s => s.stage_type === "won"))
            errs.push("A Won stage is required (probability = 100).");
        if (!stageList.find(s => s.stage_type === "lost"))
            errs.push("A Lost stage is required (probability = 0).");

        return errs;
    };

    // ── save ─────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        const errs = validate(stages);
        setErrors(errs);
        if (errs.length > 0) return;

        setSaving(true);
        try {
            const res = await update(FORECAST_CONFIG_URL, {
                stages: stages.map(({ _key, ...s }) => s), // strip internal _key
                scenario,
            });
            if (res?.status === "success") {
                ToastMsg("success", "Forecast configuration saved.");
                setErrors([]);
            } else {
                const msgs = Array.isArray(res?.message) ? res.message : [res?.message || "Failed to save"];
                setErrors(msgs);
            }
        } catch {
            ToastMsg("error", "Failed to save configuration");
        } finally {
            setSaving(false);
        }
    };

    // ── preview example probability ───────────────────────────────────────────
    const exampleBase = stages.find(s => s.stage_name === "Proposal")?.probability ?? 60;
    const bestEx  = Math.min(100, Math.max(0, exampleBase + (scenario.best_case_adjustment  || 0)));
    const worstEx = Math.min(100, Math.max(0, exampleBase + (scenario.worst_case_adjustment || 0)));

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <ContainerCard>
            {/* header */}
            <div className="flex items-center justify-between mb-5 px-1">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Forecast Configuration
                    </h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Define stage probabilities and scenario adjustments used in Sales Forecasting
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        disabled={loading}
                        className="flex items-center gap-1 text-xs"
                    >
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                        Reset
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="flex items-center gap-1 text-xs bg-sidebar-primary hover:bg-sidebar-primary/90 text-white"
                    >
                        <Save className="h-3 w-3" />
                        {saving ? "Saving…" : "Save Changes"}
                    </Button>
                </div>
            </div>

            {/* validation errors */}
            {errors.length > 0 && (
                <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1">
                                Please fix the following issues:
                            </p>
                            <ul className="text-xs text-red-600 dark:text-red-400 space-y-0.5 list-disc pl-4">
                                {errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
                </div>
            ) : (
                <>
                    {/* ── section 1: Stage Probability Mapping ── */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3 px-1">
                            <div>
                                <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
                                    Stage Probability Mapping
                                </h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Set win probability per stage. Won = 100% and Lost = 0% are locked.
                                </p>
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={addStage}
                                className="flex items-center gap-1 text-xs"
                            >
                                <Plus className="h-3 w-3" />
                                Add Stage
                            </Button>
                        </div>

                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-14">#</th>
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Stage Name</th>
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-32">Stage Type</th>
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-36">Probability %</th>
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-36 text-center">
                                            Included in Forecast
                                        </th>
                                        <th className="px-3 py-2.5 w-10"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {stages.map((stage, idx) => (
                                        <tr
                                            key={stage._key}
                                            className={cn(
                                                "transition-colors",
                                                stage.is_locked
                                                    ? "bg-gray-50/60 dark:bg-gray-800/40"
                                                    : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                                            )}
                                        >
                                            {/* Order */}
                                            <td className="px-3 py-2">
                                                {stage.is_locked ? (
                                                    <span className="text-gray-400 dark:text-gray-500 font-medium">{idx + 1}</span>
                                                ) : (
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        value={stage.order}
                                                        onChange={e => updateStage(stage._key, "order", parseInt(e.target.value) || 1)}
                                                        className="w-12 text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1
                                                                   bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200
                                                                   focus:outline-none focus:ring-1 focus:ring-sidebar-primary"
                                                    />
                                                )}
                                            </td>

                                            {/* Stage Name */}
                                            <td className="px-3 py-2">
                                                {stage.is_locked ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <Lock className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            {stage.stage_name}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={stage.stage_name}
                                                        onChange={e => updateStage(stage._key, "stage_name", e.target.value)}
                                                        placeholder="Stage name…"
                                                        className="w-full min-w-[140px] text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1
                                                                   bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200
                                                                   focus:outline-none focus:ring-1 focus:ring-sidebar-primary"
                                                    />
                                                )}
                                            </td>

                                            {/* Stage Type */}
                                            <td className="px-3 py-2">
                                                {stage.is_locked ? (
                                                    <span className={cn(
                                                        "px-2 py-0.5 rounded-full text-xs font-medium capitalize",
                                                        STAGE_TYPE_STYLE[stage.stage_type]
                                                    )}>
                                                        {stage.stage_type} · locked
                                                    </span>
                                                ) : (
                                                    <select
                                                        value={stage.stage_type}
                                                        onChange={e => updateStage(stage._key, "stage_type", e.target.value)}
                                                        className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1
                                                                   bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200
                                                                   focus:outline-none focus:ring-1 focus:ring-sidebar-primary"
                                                    >
                                                        {STAGE_TYPE_OPTIONS.map(o => (
                                                            <option key={o.value} value={o.value}>{o.label}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </td>

                                            {/* Probability */}
                                            <td className="px-3 py-2">
                                                <ProbabilityInput
                                                    value={stage.probability}
                                                    locked={stage.is_locked}
                                                    onChange={v => updateStage(stage._key, "probability", v)}
                                                />
                                            </td>

                                            {/* Included in Forecast */}
                                            <td className="px-3 py-2 text-center">
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer"
                                                        checked={stage.included_in_forecast}
                                                        onChange={e => updateStage(stage._key, "included_in_forecast", e.target.checked)}
                                                    />
                                                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer
                                                                    dark:bg-gray-700 peer-checked:bg-sidebar-primary
                                                                    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                                                                    after:bg-white after:rounded-full after:h-4 after:w-4
                                                                    after:transition-all peer-checked:after:translate-x-4" />
                                                </label>
                                            </td>

                                            {/* Delete */}
                                            <td className="px-2 py-2">
                                                {!stage.is_locked && (
                                                    <button
                                                        onClick={() => deleteStage(stage._key)}
                                                        className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50
                                                                   dark:hover:bg-red-900/20 transition-colors"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* legend */}
                        <div className="flex items-center gap-4 mt-2 px-1">
                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                <Info className="h-3 w-3" />
                                <span>Stages marked as Won or Lost are locked from probability edits.</span>
                            </div>
                        </div>
                    </div>

                    {/* ── section 2: Scenario Configuration ── */}
                    <div>
                        <div className="mb-3 px-1">
                            <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
                                Scenario Logic
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Additive offsets applied to stage probabilities. Final probability is capped 0–100.
                                Won/Lost always remain at 100% / 0%.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {/* Worst Case */}
                            <ScenarioCard
                                title="Worst Case"
                                description="Conservative estimate — reduces each stage's probability"
                                colorClass="border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 text-gray-800 dark:text-gray-200"
                                example={`e.g. ${exampleBase}% (Proposal) → ${worstEx}%`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                        Adjustment:
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            min={-100}
                                            max={0}
                                            value={scenario.worst_case_adjustment}
                                            onChange={e =>
                                                setScenario(prev => ({
                                                    ...prev,
                                                    worst_case_adjustment: clamp(e.target.value, -100, 0),
                                                }))
                                            }
                                            className="w-20 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1
                                                       bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200
                                                       focus:outline-none focus:ring-1 focus:ring-red-400"
                                        />
                                        <span className="text-xs text-gray-500">%</span>
                                    </div>
                                </div>
                            </ScenarioCard>

                            {/* Most Likely */}
                            <ScenarioCard
                                title="Most Likely"
                                description="Baseline — uses stage probability as-is, no adjustment"
                                colorClass="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 text-gray-800 dark:text-gray-200"
                                example={`e.g. ${exampleBase}% (Proposal) → ${exampleBase}%`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                        Adjustment:
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                        <CheckCircle2 className="h-4 w-4 text-blue-500" />
                                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                            ±0% (fixed)
                                        </span>
                                    </div>
                                </div>
                            </ScenarioCard>

                            {/* Best Case */}
                            <ScenarioCard
                                title="Best Case"
                                description="Optimistic estimate — increases each stage's probability"
                                colorClass="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 text-gray-800 dark:text-gray-200"
                                example={`e.g. ${exampleBase}% (Proposal) → ${bestEx}%`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                        Adjustment:
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={scenario.best_case_adjustment}
                                            onChange={e =>
                                                setScenario(prev => ({
                                                    ...prev,
                                                    best_case_adjustment: clamp(e.target.value, 0, 100),
                                                }))
                                            }
                                            className="w-20 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1
                                                       bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200
                                                       focus:outline-none focus:ring-1 focus:ring-green-400"
                                        />
                                        <span className="text-xs text-gray-500">%</span>
                                    </div>
                                </div>
                            </ScenarioCard>
                        </div>
                    </div>
                </>
            )}
        </ContainerCard>
    );
};

export default ForecastConfig;
